import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Checkbox, FormControlLabel,
  Divider, CircularProgress, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useTranslation } from 'react-i18next';

// Versione corrente del disclaimer — incrementa per forzare ri-accettazione
export const DISCLAIMER_VERSION = '1.0';
const COLL = 'ai_acceptances';

/** Controlla se l'utente ha già accettato la versione corrente con "non chiedere più" */
export async function hasAcceptedDisclaimer(userId) {
  if (!userId) return false;
  try {
    const snap = await getDoc(doc(db, COLL, userId));
    if (!snap.exists()) return false;
    const data = snap.data();
    return data.disclaimerVersion === DISCLAIMER_VERSION && data.dontAskAgain === true;
  } catch {
    return false;
  }
}

/** Salva l'accettazione su Firestore */
export async function saveDisclaimerAcceptance(userId, teamId, dontAskAgain) {
  if (!userId) return;
  await setDoc(doc(db, COLL, userId), {
    userId,
    teamId:             teamId || null,
    disclaimerVersion:  DISCLAIMER_VERSION,
    acceptedAt:         serverTimestamp(),
    dontAskAgain:       !!dontAskAgain,
    userAgent:          navigator.userAgent,
  });
}

export default function AiDisclaimerDialog({ open, onAccept, onCancel, userId, teamId }) {
  const { t, i18n } = useTranslation('race_engineer');
  const [dontAsk, setDontAsk]   = useState(false);
  const [saving, setSaving]     = useState(false);

  // Reset checkbox ogni volta che si apre
  useEffect(() => { if (open) setDontAsk(false); }, [open]);

  const handleAccept = useCallback(async () => {
    setSaving(true);
    try {
      await saveDisclaimerAcceptance(userId, teamId, dontAsk);
      onAccept();
    } catch (e) {
      console.error('Disclaimer save error:', e);
      onAccept(); // procedi comunque
    } finally {
      setSaving(false);
    }
  }, [userId, teamId, dontAsk, onAccept]);

  const isEN = i18n.language?.startsWith('en');

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth
      PaperProps={{ sx: { border: '1px solid', borderColor: 'warning.dark' } }}>

      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {isEN ? 'AI Disclaimer — Important Notice' : 'Disclaimer AI — Avviso Importante'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isEN ? `Version ${DISCLAIMER_VERSION}` : `Versione ${DISCLAIMER_VERSION}`}
          </Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Intro */}
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {isEN
              ? 'The MotorSportSuite AI coaching system uses Artificial Intelligence technologies that may produce errors, inaccurate or incomplete information.'
              : 'Il sistema di coaching AI di MotorSportSuite utilizza tecnologie di Intelligenza Artificiale che possono commettere errori e produrre informazioni inaccurate o incomplete.'
            }
          </Typography>

          {/* I consigli AI */}
          <Box sx={{ bgcolor: 'rgba(255,167,38,0.08)', border: '1px solid', borderColor: 'warning.dark', borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <GavelIcon sx={{ fontSize: 16, color: 'warning.main' }} />
              {isEN ? 'AI recommendations:' : 'I consigli forniti dall\'AI:'}
            </Typography>
            <List dense disablePadding>
              {(isEN ? [
                'Do NOT replace the judgment of a qualified professional race engineer',
                'Are to be considered as indicative support, NOT as binding instructions',
                'Do not account for all safety factors specific to your situation',
                'May be based on incomplete or incorrect telemetry data',
              ] : [
                'NON sostituiscono il giudizio di un ingegnere di pista qualificato',
                'Sono da intendersi come supporto orientativo, NON come indicazioni vincolanti',
                'Non tengono conto di tutti i fattori di sicurezza specifici della situazione',
                'Possono basarsi su dati di telemetria incompleti o non corretti',
              ]).map((item, i) => (
                <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'warning.main', mt: 0.5 }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography variant="body2">{item}</Typography>} />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Esclusione responsabilità */}
          <Box sx={{ bgcolor: 'rgba(244,67,54,0.06)', border: '1px solid', borderColor: 'error.dark', borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              {isEN ? 'Limitation of Liability:' : 'Esclusione di Responsabilità:'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              {isEN
                ? 'MotorSportSuite, its developers and collaborators assume NO LIABILITY for damages to persons, vehicles or property resulting from the application of AI recommendations, driving or setup decisions based on generated analyses, or any accidents or injuries related to the use of this data.'
                : 'MotorSportSuite, i suoi sviluppatori e collaboratori NON si assumono alcuna responsabilità per danni a persone, veicoli o cose derivanti dall\'applicazione dei consigli AI, decisioni di guida o setup basate sulle analisi generate, o eventuali incidenti o infortuni correlati all\'uso di questi dati.'
              }
            </Typography>
          </Box>

          {/* Sicurezza */}
          <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary', lineHeight: 1.6 }}>
            {isEN
              ? '⚠️ Motorsport activities involve inherent risks. The user is solely responsible for their own safety and that of third parties. Always follow the instructions of qualified professionals on-site.'
              : '⚠️ Le attività motorsport comportano rischi intrinseci. L\'utente è l\'unico responsabile della propria sicurezza e di quella di terzi. Segui sempre le indicazioni dei professionisti qualificati presenti in pista.'
            }
          </Typography>

          <Divider />

          {/* Don't ask again */}
          <FormControlLabel
            control={
              <Checkbox
                checked={dontAsk}
                onChange={e => setDontAsk(e.target.checked)}
                color="secondary"
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                {isEN
                  ? 'I have read and understood this disclaimer — don\'t show again for this version'
                  : 'Ho letto e compreso questo disclaimer — non mostrare più per questa versione'
                }
              </Typography>
            }
          />
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
        <Button onClick={onCancel} color="inherit" disabled={saving}>
          {isEN ? 'Cancel' : 'Annulla'}
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleAccept}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
          sx={{ minWidth: 160 }}
        >
          {saving
            ? (isEN ? 'Saving…' : 'Salvataggio…')
            : (isEN ? 'I Accept and Continue' : 'Accetto e Continuo')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
}
